import type { RequestOptions } from '@@/plugin-request/request';
import type { AxiosResponse } from '@umijs/max';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import auth from '@/services/auth';
import { message } from '@/utils/antdStatic';

const loginPath = '/user/login';

/** 与后端约定的统一响应结构 */
interface ResponseStructure {
  code: number;
  message: string;
  data: any;
}

const getDefaultHeaders = (config: RequestOptions) => {
  const token = auth.session.getAuthToken();
  const isFormRequest =
    config.requestType === 'form' ||
    (typeof FormData !== 'undefined' && config.data instanceof FormData);

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(config.data !== undefined && !isFormRequest
      ? { 'Content-Type': 'application/json' }
      : {}),
  };
};

const buildLoginRedirectPath = (pathname: string, search = '') => {
  const params = new URLSearchParams({ redirect: `${pathname}${search}` });
  return `${loginPath}?${params.toString()}`;
};

/**
 * @name 错误处理
 * 后端始终返回 HTTP 200，业务异常通过响应体 code 字段标识。
 *
 * Umi 的 errorThrower 只在 data.success === false 时触发（为 { success } 格式设计）。
 * 我们后端用 { code } 格式，因此在 responseInterceptors 中自行检测并抛出 BizError，
 * 由 axios 的 catch 链路交给 errorHandler 统一处理。
 *
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  errorConfig: {
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.name === 'BizError') {
        const info = error.info as ResponseStructure;
        if (info.code === 401) {
          auth.session.clearSession();
          const { pathname, search } = history.location;
          history.push(buildLoginRedirectPath(pathname, search));
        } else {
          message.error(info.message || '请求失败');
        }
        throw error;
      }

      // HTTP 层错误（服务器宕机、网络中断等）
      if (error.response) {
        message.error(`服务器异常：${error.response.status}`);
      } else if (error.request) {
        message.error('网络异常，请检查网络后重试');
      } else {
        message.error('请求发送失败，请稍后重试');
      }
      throw error;
    },
  },

  // 响应拦截器：将 code !== 200 的业务错误转为 BizError，触发 errorHandler
  responseInterceptors: [
    (response: AxiosResponse) => {
      const data = response.data as ResponseStructure;
      if (data?.code !== undefined && data.code !== 200) {
        const error: any = new Error(data.message || '请求失败');
        error.name = 'BizError';
        error.info = data;
        throw error;
      }
      return response;
    },
  ],

  // 请求拦截器：注入 Bearer Token
  requestInterceptors: [
    (config: RequestOptions) => {
      return {
        ...config,
        headers: {
          ...getDefaultHeaders(config),
          ...config.headers,
        },
      };
    },
  ],
};
